import {useNotificationContext} from 'platform/components';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {ReactNode, useEffect, useRef} from 'react';
import {useDispatch} from 'react-redux';

import {promptBridgeExtendedApi, useGetTenantQuery} from '@dms/api/core';
import {useGetCurrentUserInfoQuery} from '@dms/api/customer';

import {AsynchronousUseCaseType} from '@prompt-bridge/shared/validations';

import {parseDate} from 'shared/web';

import {MESSAGE_SUBSCRIPTION} from './graphql/MESSAGE_SUBSCRIPTION';
import {ExtractedDocumentStatus} from './types/ExtractedDocumentStatus';
import {promptBridgeAppSync} from './utils/promptBridgeAppSync';
import {promptBridgeNotificationsFactory} from './utils/promptBridgeNotificationsFactory';

interface PromptBridgeProviderProps {
  children: ReactNode;
}

export const PromptBridgeProvider = (props: PromptBridgeProviderProps) => {
  const {data: tenant} = useGetTenantQuery();
  const {data: currentUser} = useGetCurrentUserInfoQuery();

  const {notifications, showNotification, updateNotification, hideNotification} =
    useNotificationContext();

  const dispatch = useDispatch();

  const formatDateTime = useDateTimeFormatter();

  // Use ref to avoid recreating subscription when notifications change
  const notificationsRef = useRef(notifications);
  notificationsRef.current = notifications;

  useEffect(() => {
    if (!tenant?.id || !currentUser?.id) {
      return;
    }

    const promptBridgeSubscription = promptBridgeAppSync
      .graphql({
        query: MESSAGE_SUBSCRIPTION,
        variables: {
          tenantId: tenant.id,
          userId: currentUser.id,
          branchId: null,
        },
      })
      .subscribe({
        next: (response) => {
          if (!response?.data || !response?.data?.messageSubscription) {
            return;
          }

          dispatch(
            promptBridgeExtendedApi.util.invalidateTags([{type: 'PromptBridgeMessage', id: 'LIST'}])
          );

          const NOTIFICATION_ID = response.data.messageSubscription.messageId;

          const payload = response.data.messageSubscription.payload;

          const createdAt = formatDateTime(
            'dateTimeShort',
            parseDate(response.data.messageSubscription.createdAt)
          );

          const useCaseType = response.data.messageSubscription
            .useCaseType as AsynchronousUseCaseType;

          const gqlMessageStatus = match<
            'PENDING' | 'PROCESSING' | 'SUCCESS' | 'FAILED',
            ExtractedDocumentStatus
          >(response.data.messageSubscription.status)
            .with('PENDING', () => 'pending')
            .with('PROCESSING', () => 'processing')
            .with('SUCCESS', () => 'success')
            .with('FAILED', () => 'error')
            .exhaustive();

          return promptBridgeNotificationsFactory(useCaseType, {
            notifications: notificationsRef.current,
            showNotification,
            updateNotification,
            hideNotification,
            notificationId: NOTIFICATION_ID,
            status: gqlMessageStatus,
            createdAt,
            payload,
          });
        },
        error: (error) => {
          console.warn('PromptBridge subscription error', error.message);
          // TODO: when the subscription is ready in all environments
          // showNotification({
          //   title: 'PromptBridge subscription error',
          //   message: error.message,
          //   variant: 'warning',
          // });
        },
      });

    return () => {
      promptBridgeSubscription.unsubscribe();
    };
    // We want to run this effect only on tenant and user change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant?.id, currentUser?.id]);

  return <>{props.children}</>;
};
